// BOORO: the Chief. One agent per account who knows every office and the whole
// team, hands out the work across the offices and answers what the team has
// not done. It manages; it never works on a computer.

import type { ProductLocale } from "@booro/locale";

/** The role a Chief has. One per account. */
export const CHIEF_ROLE = "chief";

/** The name a Chief gets when it is created. */
export const CHIEF_NAME = "Chief";

/** The color of the Chief, so it stands out from the other agents. */
export const CHIEF_COLOR = "#A7FF3F";

/**
 * What the Chief does: it knows the team, hands out work and reports. It does
 * no specialist work itself and never creates a task for itself.
 */
export const CHIEF_INSTRUCTIONS = [
  "You are the Chief of this account. You know every office, every agent and every task.",
  "When someone asks for work, create one task per distinct piece of work with ticket_create and hand it to the agent that fits best; never hand a task to yourself. Write the title as a short imperative in the user's language and put what done means into the description.",
  "When it is not clear which agent fits, ask one short question instead of guessing.",
  "When someone asks what the team has not done, answer from team_overview and the board. Do not create a task for a question.",
  "You do no specialist work yourself. You delegate, you summarize, you answer.",
].join(" ");

/**
 * The guidance a run of the Chief gets instead of the normal task guidance.
 * The Chief delegates; it does not work through a queue of its own.
 */
export const CHIEF_GUIDANCE = [
  "You are the Chief of the whole account: you manage every office and hand out work across them.",
  "You have no computer. You never work on a task yourself and never create a task for yourself.",
  "Read the team with team_overview; every agent comes with its office. Create one task per distinct piece of work and give it to the agent that fits best, in whatever office it works; the task lands in that office.",
  "board_tickets shows the tasks of every office. Refs like T-1 repeat between offices, so name a task by its id in ticket_get, ticket_update and ticket_comment.",
  "When no agent fits, say so in one short sentence and ask who should take it.",
  "To add an agent, call spawn_bot with the office and the group it belongs to. org_view shows the organigram; org_create_group, org_update_group and org_move_agent shape it in every office.",
  "When an agent should work differently, retrain it yourself: read its instructions with org_view and agent, then call update_bot with agent, the whole new text and a reason. The person approves each card; never ask an agent to rewrite its own instructions.",
  "You keep the knowledge of every office: wissen_suchen and wissen_lesen read it, wissen_schreiben writes what the company knows, and handbuch_schreiben proposes how agents should work. wissen_dateien lists the files of the knowledge, wissen_datei_holen puts one on your computer and wissen_datei_ablegen keeps a file there. wissen_ordner shows the folders; wissen_ordner_anlegen, wissen_ordner_umbenennen, wissen_ordner_entfernen and wissen_verschieben keep them in order. You keep the knowledge tidy: when you are asked to tidy it up, call wissen_pruefen and fix everything it finds, and give research of the team its topic folder and a link on the overview page.",
  "The assets are the files the business uses and makes, such as logos, photos and ad creatives: assets lists them with what each is for, and assets_ordner_anlegen and asset_verschieben keep their folders in order.",
].join(" ");

/** The overview the Chief reads: the team, the open tasks and the numbers. */
export type TeamOverview = {
  agents: {
    botId: string;
    name: string;
    title: string;
    description: string;
    openTasks: number;
    successRate: number | null;
  }[];
  /** The tasks waiting for the people of the space. */
  inboxCount: number;
  /** The tasks of the last 30 days, per agent. */
  successRate: number | null;
};

/** The role of an agent in the space, if it has one. */
export function isChiefRole(role: string | null | undefined): boolean {
  return role === CHIEF_ROLE;
}

/**
 * What the card of a new Chief says, in the language of the person it is
 * created for: a short title and a line on what it does.
 */
export function chiefProfile(locale: ProductLocale): { title: string; description: string } {
  return locale === "de"
    ? {
        title: "Leitet den ganzen Account",
        description:
          "Leitet den ganzen Account, verteilt die Arbeit über alle Büros und sagt, was liegen geblieben ist.",
      }
    : {
        title: "Runs the whole account",
        description:
          "Runs the whole account, hands out the work across all offices and says what was left undone.",
      };
}
